import { AgTechClickEventHandler, AgTechButtonProps, AgTechButton } from "@agtech/web/components/Buttons/AgTechButtons";
import { AgTechFormSection } from "@agtech/web/components/Forms/Layout/AgTechFormSection";
import { buildGridEntitySelectionIndex, markGridSelectionEntityAsSelected, markGridSelectionEntityAsUnselected } from "@agtech/web/components/Grids/AgTechGridEntities";
import { useDiscountDataWriteDiscountInvoiceChangesAction, ChangeItemOnInvoiceDTO } from "@sales/data/lots/invoices/LotInvoiceDataActions";
import InvoiceChangeDiscountsGrid from "../../components/grids/InvoiceChangeDiscountsGrid";
import AgTechFormDialog from "@agtech/web/components/Portals/Dialogs/AgTechFormDialog";
import { useAgTechQuery, useAgTechQueryResultCombination } from "@agtech/core/data/queries/AgTechDataQueries";
import { DiscountDataGetAllDiscountsQuery } from "@sales/data/sales/discounts/SaleDiscountDataQueries";
import AgTechFormBody from "@agtech/web/components/Forms/Layout/AgTechFormBody";
import { AgTechFormRow } from "@agtech/web/components/Forms/Layout/AgTechFormRow";
import { AgTechFormReadOnlyTextControl } from "@agtech/web/components/Forms/Controls/AgTechFormReadOnlyTextControl";
import { AgTechMoneyFormatter } from "@agtech/web/utilities/AgTechUtilities";
import SaleContactDetailPopUp from "../../../contacts/popups/detail/SaleContactDetailPopUp";
import { useInvoiceDataGetInvoiceDetailsQuery } from "@sales/data/invoicing/InvoicingDataQueries";
import { calculateDiscountTotal } from "@sales/data/invoicing/InvoicingData";
import { getContactFullName } from "@agtech/data/contacts/ContactData";
import ContactDialog from "@agtech/web/integrations/contacts/dialogs/ContactDialog";

export type InvoiceChangeDiscountsDialogProps = {
    invoiceId: number,
    contactId: number
}

const InvoiceChangeDiscountsDialog = (props: InvoiceChangeDiscountsDialogProps) => {
    const dialogQuery = useInvoiceChangeDiscountsDialogQuery({
        invoiceId: props.invoiceId
    });

    const dialogAction = useDiscountDataWriteDiscountInvoiceChangesAction(props);

    return (
        <AgTechFormDialog
            width={600}
            dialogData={{
                query: dialogQuery,
                getDialogData: d => {
                    const allSaleDiscounts = Object.values(d.allDiscounts);
                    const invoiceDiscounts = d.invoiceDetails.invoice.details.discountsGiven;

                    return buildGridEntitySelectionIndex({
                        entities: allSaleDiscounts,
                        isSelected: l => invoiceDiscounts.find((elem) => elem.id === l.id) !== undefined
                    });
                },
                getDialogContext: dialogData => {
                    return {
                        invoice: dialogData.invoiceDetails.invoice,
                        contact: dialogData.invoiceDetails.contact,
                        appliedDiscounts: dialogData.invoiceDetails.invoice.details.discountsGiven,
                        allDiscounts: dialogData.allDiscounts
                    }
                }
            }} 
            dialogAction={{
                action: dialogAction,
                getActionData: d => {
                    const discountSelections = Object.values(d);
                    const updatedDiscounts = discountSelections.filter(d => d.isSelected != d.wasOriginallySelected);

                    return updatedDiscounts.map<ChangeItemOnInvoiceDTO>(l => ({
                        remove: l.wasOriginallySelected == true && l.isSelected == false,
                        id: l.entity.id
                    }));
                }
            }}
            structure={(data) => ({
                header: `Update Discounts - Invoice #${data.context.invoice.info.number}`,
                submitButtonText: 'Submit',
            })}
         >
            {(formContext) => {
                const selectedDiscounts = Object.values(formContext.entity).filter(d => d.isSelected);

                const lotTotal = formContext.context.invoice.details.lotsPurchased.reduce((acc, curr) => acc + (curr.purchasePrice ?? 0), 0);
                const creditsTotal = formContext.context.invoice.details.creditsApplied.reduce((acc, curr) => acc + (curr.creditAmount ?? 0), 0);
                const updatedDiscountsTotal = calculateDiscountTotal(lotTotal, selectedDiscounts.map(d => d.entity));
            
                const invoiceTotal = lotTotal - updatedDiscountsTotal - creditsTotal;

                return (
                    <AgTechFormBody>
                        <AgTechFormRow>
                            <AgTechFormReadOnlyTextControl
                                entityType={formContext.entity}
                                caption="Contact Name"
                                value={e => getContactFullName(formContext.context.contact.info)}
                                onClick={e => ContactDialog.Open(e, {
                                    contactId: formContext.context.contact.id
                                })}
                                showPopUpOnHover={e => ({
                                    popup: () => <SaleContactDetailPopUp contactId={formContext.context.contact.id} />
                                })}
                            />
                        </AgTechFormRow>
                        <AgTechFormRow>
                            <AgTechFormReadOnlyTextControl
                                entityType={formContext.entity}
                                caption='Lots sold'
                                value={e => AgTechMoneyFormatter.format(lotTotal)}
                                classes='snug'
                            />
                            <h1 className='pt-4 font-semibold text-lg tall centered flex'>-</h1>
                            <AgTechFormReadOnlyTextControl
                                entityType={formContext.entity}
                                caption='Discounts Given'
                                value={e => AgTechMoneyFormatter.format(updatedDiscountsTotal)}
                                classes='snug'
                            />
                            <h1 className='pt-4 font-semibold text-lg tall centered flex'>-</h1>
                            <AgTechFormReadOnlyTextControl
                                entityType={formContext.entity}
                                caption='Credits Applied'
                                value={e => AgTechMoneyFormatter.format(creditsTotal)}
                                classes='snug'
                            />
                            <h1 className='pt-4 font-semibold text-lg tall centered flex'>=</h1>
                            <AgTechFormReadOnlyTextControl
                                entityType={formContext.entity}
                                caption='Invoice Amount'
                                value={e => AgTechMoneyFormatter.format(invoiceTotal)}
                                classes='snug'
                            />
                        </AgTechFormRow>
                        <AgTechFormSection
                            header="Discounts"
                            classes='flexed'
                            bodyClasses='flexed pt-3'
                        >
                            <InvoiceChangeDiscountsGrid
                                discountSelections={formContext.entity}
                                onDiscountSelected={async discount => {
                                    formContext.updateFormEntity(curr => {
                                        return markGridSelectionEntityAsSelected({
                                            selections: curr,
                                            entity: {
                                                ...discount
                                            }
                                        });
                                    });
                                }}
                                onDiscountDeselected={async discount => {
                                    formContext.updateFormEntity(curr => {
                                        return markGridSelectionEntityAsUnselected({
                                            selections: formContext.entity,
                                            entity: {
                                                ...discount
                                            }
                                        });
                                    });
                                }}
                            />
                        </AgTechFormSection>
                    </AgTechFormBody>
                )
            }}
        </AgTechFormDialog>
    )
}

const useInvoiceChangeDiscountsDialogQuery = (props: { invoiceId: number }) => {
    const invoiceDetailsQuery = useInvoiceDataGetInvoiceDetailsQuery({
        invoiceId: props.invoiceId
    });

    const discountsQuery = useAgTechQuery({
        query: DiscountDataGetAllDiscountsQuery
    });

    return useAgTechQueryResultCombination({
        firstQuery: invoiceDetailsQuery,
        secondQuery: discountsQuery,
        projection: (invoiceDetails, allDiscounts) => {
            return {
                invoiceDetails,
                allDiscounts
            }
        }
    });
}

InvoiceChangeDiscountsDialog.Open = (e: AgTechClickEventHandler, props: InvoiceChangeDiscountsDialogProps) => e.dialogs.openDialog({
    dialog: () => <InvoiceChangeDiscountsDialog {...props} />
});

InvoiceChangeDiscountsDialog.Button = (props: AgTechButtonProps & InvoiceChangeDiscountsDialogProps) => {
    let buttonProps: AgTechButtonProps = {
        text: 'Change Discounts',
        icon: 'bi bi-arrow-left-right',
        ...props,
        action: e => InvoiceChangeDiscountsDialog.Open(e, props)
    }

    return <AgTechButton {...buttonProps} />
}

export default InvoiceChangeDiscountsDialog;